import { AnimatePresence, motion } from 'motion/react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { AssignSheet } from '../components/AssignSheet'
import { ExpeditionCard } from '../components/ExpeditionCard'
import { LocationLootPreview } from '../components/LocationLootPreview'
import { locationCardBackdrop } from '../game/locationBackgrounds'
import { getItemDef, RARITY_COLOR } from '../game/items'
import { useI18n } from '../i18n/useI18n'
import { maxConcurrentExpeditions } from '../game/hideoutProgression'
import { getPortraitSrc } from '../game/portraits'
import {
  expeditionParticipantIds,
  getLocationExplorationPct,
  isLocationUnlocked,
  LOCATION_LIST,
  REGION_LIST,
} from '../game/locations'
import {
  isA2RegionVisibleInTrips,
  isDenBoschRegionVisibleInTrips,
  isUtrechtRegionVisibleInTrips,
} from '../game/quests'
import { CORE_SKILLS, getSkillLevel } from '../game/skills'
import {
  garageCanFitVehicle,
  maxGarageSlots,
  scrapYieldForVehicleDef,
  totalGarageSlotsUsed,
  vehicleIsOnActiveExpedition,
} from '../game/garageOps'
import { estimateLootYieldVsBaselinePct } from '../game/loot'
import { getMaxSurvivors } from '../game/lodging'
import { getVehicleDef, vehicleLootYieldParams } from '../game/vehicles'
import type {
  Expedition,
  ItemStack,
  LocationId,
  RegionId,
  SkillKey,
  Survivor,
} from '../game/types'
import { useGameStore } from '../store/useGameStore'

const PHASES_COLLAPSED_KEY = 'expedition_phases_collapsed'

function loadCollapsedState(): Partial<Record<RegionId, boolean>> {
  try {
    const raw = localStorage.getItem(PHASES_COLLAPSED_KEY)
    if (raw) return JSON.parse(raw) as Partial<Record<RegionId, boolean>>
  } catch {
    // ignore
  }
  return {}
}

function saveCollapsedState(state: Partial<Record<RegionId, boolean>>) {
  try {
    localStorage.setItem(PHASES_COLLAPSED_KEY, JSON.stringify(state))
  } catch {
    // ignore
  }
}

export function ExpeditionsScreen() {
  const { language, t, pick, formatDurationMs, formatDateTime } = useI18n()
  const survivors = useGameStore((s) => s.survivors)
  const expeditions = useGameStore((s) => s.expeditions)
  const hideoutTierId = useGameStore((s) => s.hideoutTierId)
  const progress = useGameStore((s) => s.progress)
  const locationExploration = useGameStore((s) => s.locationExploration)
  const quests = useGameStore((s) => s.quests)
  const completeTripReport = useGameStore((s) => s.completeTripReport)
  const idleCount = survivors.filter((s) => s.status === 'Idle').length
  const [collapsedRegions, setCollapsedRegions] = useState<Partial<Record<RegionId, boolean>>>(loadCollapsedState)
  const [presetLocationId, setPresetLocationId] = useState<LocationId | null>(null)
  const [reportExpeditionId, setReportExpeditionId] = useState<string | null>(null)
  const [newlyUnlockedLocationIds, setNewlyUnlockedLocationIds] = useState<LocationId[]>([])
  const [confirmedUnlockedLocationIds, setConfirmedUnlockedLocationIds] = useState<LocationId[]>(
    [],
  )
  const unlockAnimationStartTimerRef = useRef<number | null>(null)
  const unlockAnimationEndTimerRef = useRef<number | null>(null)
  const activeExpeditions = expeditions.filter((exp) => exp.status === 'active')
  const expeditionCap = maxConcurrentExpeditions(hideoutTierId)
  const atExpeditionCap = activeExpeditions.length >= expeditionCap
  const returnedExpeditions = expeditions.filter(
    (exp) => exp.status === 'returned' && exp.report,
  )
  const pendingUnlockedLocationIds = useMemo(() => {
    const ids = new Set<LocationId>()
    for (const expedition of returnedExpeditions) {
      for (const locationId of expedition.report?.unlockedLocationIds ?? []) {
        ids.add(locationId)
      }
    }
    return [...ids]
  }, [returnedExpeditions])
  const hiddenPendingUnlockedIds = useMemo(() => {
    const confirmed = new Set(confirmedUnlockedLocationIds)
    return pendingUnlockedLocationIds.filter((locationId) => !confirmed.has(locationId))
  }, [confirmedUnlockedLocationIds, pendingUnlockedLocationIds])
  const reportExpedition = returnedExpeditions.find((exp) => exp.id === reportExpeditionId) ?? null

  const denBoschTripsVisible = isDenBoschRegionVisibleInTrips(quests)
  const a2TripsVisible = isA2RegionVisibleInTrips(quests)
  const utrechtTripsVisible = isUtrechtRegionVisibleInTrips(quests)
  const anyDenBoschUnlocked = denBoschTripsVisible && LOCATION_LIST.some(
    (loc) =>
      loc.region === 'den-bosch' &&
      isLocationUnlocked(loc.id, progress, locationExploration, quests) &&
      !hiddenPendingUnlockedIds.includes(loc.id),
  )
  const isCollapsible = anyDenBoschUnlocked

  useEffect(() => {
    if (!isCollapsible) return
    setCollapsedRegions((prev) => {
      if ('rural-south' in prev) return prev
      const next = { ...prev, 'rural-south': true }
      saveCollapsedState(next)
      return next
    })
  }, [isCollapsible])

  const toggleRegion = (regionId: RegionId) => {
    setCollapsedRegions((prev) => {
      const next = { ...prev, [regionId]: !prev[regionId] }
      saveCollapsedState(next)
      return next
    })
  }

  useEffect(() => {
    return () => {
      if (unlockAnimationStartTimerRef.current != null) {
        window.clearTimeout(unlockAnimationStartTimerRef.current)
      }
      if (unlockAnimationEndTimerRef.current != null) {
        window.clearTimeout(unlockAnimationEndTimerRef.current)
      }
    }
  }, [])

  return (
    <div className="space-y-3">
      <section>
        <h2 className="mb-1 text-xs font-semibold uppercase tracking-[0.2em] text-stone-500">
          {t('exp_heading_active')}
        </h2>
        {activeExpeditions.length === 0 ? (
          <p className="rounded-md border border-stone-800 bg-stone-800/50 p-2 text-xs text-stone-500">
            {t('exp_none_caption')}
          </p>
        ) : (
          <div className="space-y-2">
            {activeExpeditions.map((exp) => (
              <ExpeditionCard
                key={exp.id}
                expedition={exp}
                survivors={survivors}
              />
            ))}
          </div>
        )}
      </section>

      {returnedExpeditions.length > 0 && (
        <section>
          <h2 className="mb-1 text-xs font-semibold uppercase tracking-[0.2em] text-amber-500/90">
            {t('exp_heading_returned')}  <span className="text-stone-400 text-[8px]">{t('exp_click_to_collect')}</span>
          </h2>
          <div className="space-y-2">
            <AnimatePresence initial={false}>
              {returnedExpeditions.map((exp) => {
              const teamIds = expeditionParticipantIds(exp)
              const team = teamIds
                .map((id) => survivors.find((s) => s.id === id))
                .filter((s): s is Survivor => !!s)
              const survivor = team[0]
              const teaserNames = team.map((s) => s.name)
              const extraCount = Math.max(0, teaserNames.length - 3)
              const location = LOCATION_LIST.find((loc) => loc.id === exp.locationId)
              const teaserItems = pickLootTeaserItems(exp.report?.collectedItems ?? [])
              const teaserHiddenCount = Math.max(0, teaserItems.length - 4)
              const teaserPreview = teaserItems.slice(0, 5)
              const locationName = location ? pick(location.name) : exp.locationId
              const andWord = language === 'nl' ? '&' : '&'
              const displayedTeaserName =
                teaserNames.length <= 1
                  ? teaserNames[0] ?? ''
                  : teaserNames.length === 2
                    ? `${teaserNames[0]} ${andWord} ${teaserNames[1]}`
                    : teaserNames.length === 3
                      ? `${teaserNames[0]}, ${teaserNames[1]} ${andWord} ${teaserNames[2]}`
                      : `${teaserNames[0]}, ${teaserNames[1]}, ${teaserNames[2]} +${extraCount}`
                return (
                <motion.button
                  key={exp.id}
                  type="button"
                  onClick={() => setReportExpeditionId(exp.id)}
                  className="w-full rounded-md border border-amber-900/40 bg-stone-800/70 p-2 text-left transition hover:border-amber-700 hover:bg-stone-800"
                  layout
                  initial={{ opacity: 0, y: 14, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8, scale: 0.98 }}
                  transition={{ duration: 0.22, ease: 'easeOut' }}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex flex-1 gap-2.5">
                      <ReturnedTeamPortrait team={team} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1">
                        <div className="truncate text-sm font-semibold leading-tight text-stone-100">
                          {survivor
                            ? t('exp_returned_from_location', {
                                name: displayedTeaserName,
                                location: locationName,
                              })
                            : t('exp_report_ready')}
                        </div>
                        <div className="shrink-0 pt-0.5 text-[11px] text-stone-400">
                      {exp.returnedAt ? formatDateTime(exp.returnedAt) : ''}
                    </div>
                    </div>
                        <div className="mt-1 rounded border border-stone-800/90 bg-stone-950/70 px-2 py-1">
                          {teaserPreview.length > 0 ? (
                            <div className="flex items-center gap-1.5 overflow-hidden">
                              {teaserPreview.map((stack, idx) => {
                                const def = getItemDef(stack.defId)
                                const label = def ? pick(def.name) : stack.defId
                                const rarityClass = def ? RARITY_COLOR[def.rarity] : 'text-stone-300'
                                const visibilityClass =
                                  idx < 2 ? '' : idx === 2 ? 'hidden sm:inline' : 'hidden lg:inline'
                                return (
                                  <span
                                    key={stack.defId}
                                    className={`shrink-0 rounded border border-stone-800 bg-stone-800/60 px-1.5 py-0.5 text-[10px] ${rarityClass} ${visibilityClass}`}
                                    title={`${stack.quantity}x ${label}`}
                                  >
                                    {stack.quantity}x {label}
                                  </span>
                                )
                              })}
                              {teaserHiddenCount > 0 && (
                                <span className="shrink-0 text-[10px] text-stone-500">
                                  +{teaserHiddenCount}
                                </span>
                              )} <span className="text-stone-400 text-[12px] uppercase">...</span>
                            </div>
                          ) : (
                            <div className="text-[10px] text-stone-500">{t('toast_nothing')}</div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                  </div>
                </motion.button>
                )
              })}
            </AnimatePresence>
          </div>
        </section>
      )}

      <div data-tutorial-id="expedition-locations">
      {REGION_LIST.map((region) => {
        const isDenBoschRegion = region.id === 'den-bosch'
        const isA2Region = region.id === 'a2-highway'
        const isUtrechtRegion = region.id === 'utrecht'

        let isUnlocked =
          isDenBoschRegion
            ? denBoschTripsVisible
            : isA2Region
              ? a2TripsVisible
              : isUtrechtRegion
                ? utrechtTripsVisible
                : progress >= region.unlockAt

        if (isDenBoschRegion && !denBoschTripsVisible) return null
        if (isA2Region && !a2TripsVisible) return null
        if (isUtrechtRegion && !utrechtTripsVisible) return null

        const locationsInRegion = LOCATION_LIST.filter((l) => l.region === region.id)
        const visibleLocations = locationsInRegion.filter((loc) =>
          isLocationUnlocked(loc.id, progress, locationExploration, quests) &&
          !hiddenPendingUnlockedIds.includes(loc.id),
        )
        const remaining = Math.max(0, region.unlockAt - progress)
        const tripsLeftMsg =
          remaining === 1
            ? t('exp_remaining_one', { count: remaining })
            : t('exp_remaining_many', { count: remaining })

        if (
          !isDenBoschRegion &&
          !isA2Region &&
          !isUtrechtRegion &&
          isUnlocked &&
          visibleLocations.length === 0
        ) {
          return null
        }

        const isCollapsed = isCollapsible && !!collapsedRegions[region.id as RegionId]

        return (
          <section key={region.id}>
            <div className="mb-2 flex items-stretch gap-2 rounded-md border border-stone-700/80 bg-stone-900/55 px-3 py-2.5 shadow-sm shadow-black/20">
              {isCollapsible ? (
                <button
                  type="button"
                  onClick={() => toggleRegion(region.id as RegionId)}
                  className="group flex min-w-0 flex-1 items-center justify-between gap-3 text-left text-sm font-bold uppercase tracking-[0.16em] text-stone-200 transition-colors hover:text-amber-100/95"
                >
                  <span className="truncate">{pick(region.name)}</span>
                  <span
                    className={`shrink-0 text-xs text-stone-500 transition-transform duration-200 group-hover:text-stone-400 ${isCollapsed ? '' : 'rotate-90'}`}
                    aria-hidden
                  >
                    ▶
                  </span>
                </button>
              ) : (
                <h2 className="min-w-0 flex-1 truncate text-sm font-bold uppercase tracking-[0.16em] text-stone-200">
                  {pick(region.name)}
                </h2>
              )}
              {!isDenBoschRegion &&
                !isA2Region &&
                !isUtrechtRegion &&
                !isUnlocked && (
                <span className="shrink-0 self-center text-[11px] font-medium leading-tight text-stone-500">
                  {tripsLeftMsg}
                </span>
              )}
            </div>
            {!isCollapsed && (
            <>
            {!isUnlocked ? (
              <div className="rounded-md border border-stone-800 bg-stone-800/40 p-2">
                <p className="text-xs italic leading-snug text-stone-500">{pick(region.blurb)}</p>
                <div className="mt-2 h-1.5 overflow-hidden rounded-sm bg-stone-800">
                  <div
                    className="h-full bg-stone-600 transition-[width] duration-500"
                    style={{
                      width: `${Math.min(100, (progress / region.unlockAt) * 100)}%`,
                    }}
                  />
                </div>
                <p className="mt-1 text-[10px] text-stone-600">
                  {t('exp_progress', { done: progress, need: region.unlockAt })}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {isDenBoschRegion && visibleLocations.length === 0 ? (
                  <p className="rounded-md border border-stone-800 bg-stone-800/40 p-2 text-xs italic leading-snug text-stone-500">
                    {t('exp_den_bosch_quest_gate_hint')}
                  </p>
                ) : null}
                {isA2Region && visibleLocations.length === 0 ? (
                  <p className="rounded-md border border-stone-800 bg-stone-800/40 p-2 text-xs italic leading-snug text-stone-500">
                    {t('exp_a2_quest_gate_hint')}
                  </p>
                ) : null}
                {isUtrechtRegion && visibleLocations.length === 0 ? (
                  <p className="rounded-md border border-stone-800 bg-stone-800/40 p-2 text-xs italic leading-snug text-stone-500">
                    {t('exp_utrecht_quest_gate_hint')}
                  </p>
                ) : null}
                {visibleLocations.map((loc) => {
                  const isNewlyUnlocked = newlyUnlockedLocationIds.includes(loc.id)
                  const explorationPct = getLocationExplorationPct(
                    locationExploration,
                    loc.id,
                  )
                  const clampedExplorationPct = Math.max(0, Math.min(100, explorationPct))
                  const explorationStateLabel =
                    explorationPct <= 0
                      ? t('exp_exploration_unexplored')
                      : explorationPct >= 100
                        ? t('exp_exploration_fully_explored')
                        : t('exp_exploration')
                  const lootVisible = explorationPct >= loc.exploration.lootRevealAtPct
                  const backdrop = locationCardBackdrop(loc.id)
                  return (
                    <motion.div
                      key={loc.id}
                      initial={isNewlyUnlocked ? { scale: 0.82, opacity: 0 } : false}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={
                        isNewlyUnlocked
                          ? { type: 'spring', stiffness: 380, damping: 26 }
                          : undefined
                      }
                      className={`relative overflow-hidden rounded-md border bg-stone-800/70 p-2 transition-colors ${
                        isNewlyUnlocked
                          ? 'border-amber-700/55 shadow-[0_0_18px_3px_rgba(251,191,36,0.22)]'
                          : 'border-stone-800'
                      }`}
                    >
                      <img
                        src={backdrop.src}
                        alt=""
                        aria-hidden
                        decoding="async"
                        className="pointer-events-none absolute inset-0 z-0 h-full w-full object-cover select-none"
                        style={{ filter: backdrop.brightnessFilter }}
                      />
                      <div className="absolute inset-0 z-1 bg-[radial-gradient(circle_at_center,rgba(0,0,0,0.2),rgba(0,0,0,0.88))]" />
                      <div className="relative z-2 flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start gap-2">
                            <div
                              className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-full bg-stone-950 ring-1 ring-inset ring-stone-700"
                              style={{
                                background: `conic-gradient(rgb(5 150 105) ${clampedExplorationPct}%, rgb(41 37 36) 0)`,
                              }}
                              aria-label={t('exp_exploration')}
                            >
                              <div className="grid h-7 w-7 place-items-center rounded-full bg-stone-950 text-[9px] font-mono tabular-nums text-stone-200">
                                {explorationPct}%
                              </div>
                            </div>
                            <div className="min-w-0">
                              <div className="text-sm font-semibold leading-tight text-stone-100">
                                {pick(loc.name)}
                              </div>
                              <div className="text-[10px] uppercase tracking-wider text-stone-500">
                                {explorationStateLabel}
                              </div>
                            </div>
                          </div>
                          <p className="mt-0.5 text-[11px] italic leading-snug text-stone-400">
                            {pick(loc.blurb)}
                          </p>
                        </div>
                        <span className="shrink-0 font-mono text-[10px] text-stone-500">
                          {formatDurationMs(loc.durationMs)}
                        </span>
                      </div>
                      <LocationLootPreview
                        loot={loc.loot}
                        className="relative z-2 mt-1.5"
                        obfuscated={!lootVisible}
                        recruitPreview={
                          loc.exploration.recruit
                            ? {
                                portraitKey: loc.exploration.recruit.survivor.portrait,
                                recruited: survivors.some(
                                  (s) => s.id === loc.exploration.recruit!.survivor.id,
                                ),
                              }
                            : undefined
                        }
                      />
                      <div className="relative z-2 mt-2">
                        <button
                          type="button"
                          onClick={() => setPresetLocationId(loc.id)}
                          disabled={idleCount === 0 || atExpeditionCap}
                          className="w-full rounded-md border border-amber-800/60 bg-amber-900/30 px-2 py-1.5 text-xs font-semibold uppercase tracking-wider text-amber-200 transition-colors hover:bg-amber-900/60 disabled:cursor-not-allowed disabled:border-stone-800 disabled:bg-stone-800 disabled:text-stone-600"
                        >
                          {idleCount === 0
                            ? t('exp_no_survivors')
                            : atExpeditionCap
                              ? t('exp_slots_full')
                              : t('exp_send_out')}
                        </button>
                        {(idleCount === 0 || atExpeditionCap) && (
                          <p className="mt-1 text-center text-[10px] text-stone-500">
                            {idleCount === 0
                              ? t('exp_send_disabled_idle')
                              : t('exp_send_disabled_full')}
                          </p>
                        )}
                      </div>
                    </motion.div>
                  )
                })}
              </div>
            )}
            </>
            )}
          </section>
        )
      })}
      </div>

      <AssignSheet
        key={presetLocationId ?? 'assign-sheet-closed'}
        survivor={null}
        presetLocationId={presetLocationId}
        onClose={() => setPresetLocationId(null)}
      />
      <TripReportSheet
        key={reportExpedition?.id ?? 'trip-report-closed'}
        expedition={reportExpedition}
        onClose={() => setReportExpeditionId(null)}
        onConfirmUnlockedLocations={(locationIds) => {
          if (locationIds.length === 0) return
          setConfirmedUnlockedLocationIds((prev) => [...new Set([...prev, ...locationIds])])
        }}
        onComplete={(discardedDefIds, recruitFoundSurvivor, storeFoundVehicle) => {
          if (!reportExpedition) return
          const unlockedLocationIds = reportExpedition.report?.unlockedLocationIds ?? []
          if (unlockedLocationIds.length > 0) {
            setConfirmedUnlockedLocationIds((prev) => [
              ...new Set([...prev, ...unlockedLocationIds]),
            ])
          }
          completeTripReport(
            reportExpedition.id,
            discardedDefIds,
            recruitFoundSurvivor,
            storeFoundVehicle,
          )
          setReportExpeditionId(null)
          if (unlockedLocationIds.length > 0) {
            if (unlockAnimationStartTimerRef.current != null) {
              window.clearTimeout(unlockAnimationStartTimerRef.current)
            }
            if (unlockAnimationEndTimerRef.current != null) {
              window.clearTimeout(unlockAnimationEndTimerRef.current)
            }

            unlockAnimationStartTimerRef.current = window.setTimeout(() => {
              setNewlyUnlockedLocationIds(unlockedLocationIds)
              unlockAnimationEndTimerRef.current = window.setTimeout(() => {
                setNewlyUnlockedLocationIds([])
                unlockAnimationEndTimerRef.current = null
              }, 1800)
              unlockAnimationStartTimerRef.current = null
            }, 200)
          }
        }}
      />
    </div>
  )
}

function TripReportSheet({
  expedition,
  onClose,
  onConfirmUnlockedLocations,
  onComplete,
}: {
  expedition: Expedition | null
  onClose: () => void
  onConfirmUnlockedLocations: (locationIds: LocationId[]) => void
  onComplete: (
    discardedDefIds: string[],
    recruitFoundSurvivor: boolean,
    storeFoundVehicle: boolean,
  ) => void
}) {
  const { language, t, pick, formatDateTimeShort } = useI18n()
  const itemCategoryLabel = (category: string): string => {
    switch (category) {
      case 'food':
        return t('stash_filter_food')
      case 'water':
        return t('stash_filter_water')
      case 'scrap':
        return t('stash_filter_scrap')
      case 'meds':
        return t('stash_filter_meds')
      case 'trinket':
        return t('stash_filter_trinkets')
      case 'weapon':
        return t('stash_filter_weapons')
      default:
        return category
    }
  }
  const [discarded, setDiscarded] = useState<Record<string, boolean>>({})
  const [recruitFoundSurvivor, setRecruitFoundSurvivor] = useState(true)
  const garageVehicles = useGameStore((s) => s.garageVehicles)
  const workstations = useGameStore((s) => s.workstations)
  const expeditions = useGameStore((s) => s.expeditions)
  const scrapVehicle = useGameStore((s) => s.scrapVehicle)
  const rosterSurvivors = useGameStore((s) => s.survivors)

  const report = expedition?.report
  const foundSurvivor = report?.foundSurvivor
  const foundSurvivorSkillIds = useMemo(() => {
    if (!foundSurvivor) return [] as SkillKey[]
    const unlocked = Object.entries(foundSurvivor.skills)
      .filter(([, state]) => state?.unlocked)
      .map(([skillId]) => skillId as SkillKey)
    const core = CORE_SKILLS.filter((skillId) => unlocked.includes(skillId))
    const extras = unlocked
      .filter((skillId) => !CORE_SKILLS.includes(skillId))
      .sort((a, b) =>
        t(`skill_${a}` as never).localeCompare(t(`skill_${b}` as never), language),
      )
    return [...core, ...extras]
  }, [foundSurvivor, language, t])
  const foundVehicleDef = report?.foundVehicleDefId
    ? getVehicleDef(report.foundVehicleDefId)
    : null
  const usedVehicleDef = expedition?.vehicleDefId
    ? getVehicleDef(expedition.vehicleDefId)
    : null
  const usedVehicleLootMultiplier = usedVehicleDef?.lootMultiplier ?? 1
  const locationForYield = expedition
    ? LOCATION_LIST.find((loc) => loc.id === expedition.locationId)
    : null
  /** Team average of pre-trip personal loot yield vs baseline (needs/skills only, no vehicle). */
  const usedPersonalLootYieldPct = useMemo(() => {
    if (!report || !locationForYield) return 0
    const outcomes = report.participantOutcomes
    if (outcomes?.length) {
      let sum = 0
      let n = 0
      for (const o of outcomes) {
        const surv = rosterSurvivors.find((s) => s.id === o.survivorId)
        if (!surv) continue
        sum += estimateLootYieldVsBaselinePct(
          { ...surv, needs: o.needsBefore },
          locationForYield.rolls,
        )
        n += 1
      }
      return n > 0 ? Math.round(sum / n) : 0
    }
    const surv = rosterSurvivors.find((s) => s.id === report.survivorId)
    if (!surv) return 0
    return estimateLootYieldVsBaselinePct(
      { ...surv, needs: report.needsBefore },
      locationForYield.rolls,
    )
  }, [report, locationForYield, rosterSurvivors])
  const usedVehicleLootYieldPct = Math.round((usedVehicleLootMultiplier - 1) * 100)
  const usedTotalLootYieldPct = usedPersonalLootYieldPct + usedVehicleLootYieldPct
  const explorationProgressPct = Math.round(
    report?.explorationAfterPct ?? report?.explorationBeforePct ?? 0,
  )
  const showExplorationSection =
    (report?.explorationBeforePct != null || report?.explorationAfterPct != null) &&
    explorationProgressPct < 100
  const garageBuilt = workstations.garage?.built === true
  const garageLevel = garageBuilt ? Math.max(1, workstations.garage?.level ?? 1) : 0
  const garageHasRoom =
    garageBuilt &&
    !!report?.foundVehicleDefId &&
    garageCanFitVehicle(garageLevel, garageVehicles, report.foundVehicleDefId)

  const rosterCap = getMaxSurvivors(workstations)
  const rosterFull = rosterSurvivors.length >= rosterCap

  const [storeFoundVehicle, setStoreFoundVehicle] = useState(() => {
    const sid = expedition?.report?.foundVehicleDefId
    const gb = workstations.garage?.built === true
    if (!sid || !gb) return true
    const gl = Math.max(1, workstations.garage?.level ?? 1)
    return garageCanFitVehicle(gl, garageVehicles, sid)
  })
  const [garageManageOpen, setGarageManageOpen] = useState(false)
  const [manageScrapConfirmId, setManageScrapConfirmId] = useState<string | null>(null)

  useEffect(() => {
    if (rosterFull) setRecruitFoundSurvivor(false)
    else if (foundSurvivor) setRecruitFoundSurvivor(true)
  }, [rosterFull, foundSurvivor, expedition?.id])

  const scrapableParkedVehicles = useMemo(
    () =>
      garageVehicles.filter((v) => !vehicleIsOnActiveExpedition(expeditions, v.id)),
    [garageVehicles, expeditions],
  )

  const unlockedLocations = useMemo(
    () =>
      (report?.unlockedLocationIds ?? [])
        .map((id) => LOCATION_LIST.find((location) => location.id === id))
        .filter((location): location is (typeof LOCATION_LIST)[number] => !!location),
    [report?.unlockedLocationIds],
  )
  const [showUnlockedPopup, setShowUnlockedPopup] = useState(
    unlockedLocations.length > 0,
  )
  const [isCompleting, setIsCompleting] = useState(false)
  const [visibleLootDefIds, setVisibleLootDefIds] = useState<string[]>(
    () => report?.collectedItems.map((stack) => stack.defId) ?? [],
  )
  const completedTripLocation = useMemo(() => {
    if (!expedition) return t('exp_card_unknown')
    const location = LOCATION_LIST.find((loc) => loc.id === expedition.locationId)
    return location ? pick(location.name) : expedition.locationId
  }, [expedition, pick, t])
  const reportBackdrop = locationCardBackdrop(expedition?.locationId ?? 'vinkel-bridge')

  const discardedDefIds = useMemo(
    () =>
      Object.entries(discarded)
        .filter(([, isDiscarded]) => isDiscarded)
        .map(([defId]) => defId),
    [discarded],
  )

  const toggleDiscard = (stack: ItemStack) => {
    setDiscarded((prev) => ({
      ...prev,
      [stack.defId]: !prev[stack.defId],
    }))
  }

  const toggleRecruit = (value: boolean) => {
    setRecruitFoundSurvivor(value)
  }

  const completeAfterLootAnimation = async () => {
    if (!report || isCompleting) return
    setIsCompleting(true)

    const idsToHide = [...visibleLootDefIds]
    for (const defId of idsToHide) {
      setVisibleLootDefIds((prev) => prev.filter((id) => id !== defId))
      await new Promise<void>((resolve) => {
        window.setTimeout(resolve, 100)
      })
    }

    await new Promise<void>((resolve) => {
      window.setTimeout(resolve, 300)
    })

    onComplete(
      discardedDefIds,
      recruitFoundSurvivor,
      garageBuilt && garageHasRoom ? storeFoundVehicle : false,
    )
  }

  const participantNeedSections = useMemo(() => {
    if (!report) return [] as Array<{
      survivorId: string
      displayName: string
      rows: Array<{ key: string; label: string; before: number; after: number }>
    }>
    const outcomes = report.participantOutcomes
    const rowDefs = (
      before: typeof report.needsBefore,
      after: typeof report.needsAfter,
    ) =>
      [
        {
          key: 'hunger',
          label: t('need_hunger'),
          before: before.hunger,
          after: after.hunger,
        },
        {
          key: 'thirst',
          label: t('need_thirst'),
          before: before.thirst,
          after: after.thirst,
        },
        {
          key: 'energy',
          label: t('need_energy'),
          before: before.energy,
          after: after.energy,
        },
      ].filter((row) => needValuesDiffer(row.before, row.after))

    if (outcomes?.length) {
      return outcomes
        .map((o) => ({
          survivorId: o.survivorId,
          displayName:
            rosterSurvivors.find((s) => s.id === o.survivorId)?.name ?? o.survivorId,
          rows: rowDefs(o.needsBefore, o.needsAfter),
        }))
        .filter((s) => s.rows.length > 0)
    }
    const rows = rowDefs(report.needsBefore, report.needsAfter)
    if (rows.length === 0) return []
    return [
      {
        survivorId: report.survivorId,
        displayName:
          rosterSurvivors.find((s) => s.id === report.survivorId)?.name ?? '',
        rows,
      },
    ]
  }, [report, rosterSurvivors, t])

  const tripReportTeamSubtitle = useMemo(() => {
    if (!expedition) return null
    const ids = expeditionParticipantIds(expedition)
    const teamNames = ids
      .map((id) => rosterSurvivors.find((s) => s.id === id)?.name)
      .filter((name): name is string => !!name)
    if (teamNames.length === 0) return t('exp_card_unknown')
    if (teamNames.length === 1) return t('exp_trip_report_survivor', { name: teamNames[0] })
    const andWord = language === 'nl' ? 'en' : 'and'
    if (teamNames.length === 2) return `${teamNames[0]} ${andWord} ${teamNames[1]}`
    return `${teamNames.slice(0, -1).join(', ')} ${andWord} ${teamNames[teamNames.length - 1]}`
  }, [expedition, language, rosterSurvivors, t])

  return (
    <AnimatePresence>
      {expedition && report && (
        <motion.div
          key={expedition.id}
          className="fixed inset-0 z-50 flex items-end justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <div className="absolute inset-0 bg-black/70" />
          <motion.div
            className="relative max-h-[88vh] w-full max-w-[430px] overflow-y-auto rounded-t-2xl border-t border-stone-700 bg-stone-950 p-4 pb-6 shadow-2xl"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 320, damping: 32 }}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={reportBackdrop.src}
              alt=""
              aria-hidden
              decoding="async"
              className="pointer-events-none absolute inset-0 z-0 h-full w-full object-cover opacity-50 select-none"
              style={{ filter: `${reportBackdrop.brightnessFilter} saturate(0.75)` }}
            />
            <div className="pointer-events-none absolute inset-0 z-1 bg-[radial-gradient(circle_at_center,rgba(0,0,0,0.1),rgba(0,0,0,0.3))]" />
            <div className="relative z-2">
              <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-stone-700" />
              <div className="mb-3">
              <div className="text-[10px] uppercase tracking-wider text-amber-500/90">
                {t('exp_trip_report_label')}
              </div>
              <h3 className="text-base font-semibold text-stone-100">
                {t('exp_trip_report_completed_title', { location: completedTripLocation })}
              </h3>
              <div className="mt-1 text-xs text-stone-500">
                {tripReportTeamSubtitle ?? t('exp_card_unknown')}{' '}
                ·{' '}
                {expedition.returnedAt
                  ? formatDateTimeShort(expedition.returnedAt)
                  : t('exp_trip_report_now')}
              </div>
            </div>

            {showExplorationSection && (
              <section className="mb-3 rounded-md border border-stone-800 bg-stone-800/60 p-3">
                <div className="mb-2 text-[10px] uppercase tracking-wider text-stone-500">
                  {t('exp_exploration')}
                </div>
                <div className="flex items-center justify-between text-xs text-stone-400">
                  <span>
                    {Math.round(report.explorationBeforePct ?? 0)}% →{' '}
                    {Math.round(report.explorationAfterPct ?? 0)}%
                  </span>
                  <span className="font-mono text-emerald-300">
                    +{Math.max(0, Math.round(report.explorationDeltaPct ?? 0))}%
                  </span>
                </div>
                {(report.unlockedLocationIds?.length ?? 0) > 0 && (
                  <div className="mt-2 rounded border border-emerald-900/60 bg-emerald-950/20 p-2 text-xs text-emerald-200">
                    <div className="text-[10px] uppercase tracking-wider text-emerald-300/90">
                      {t('exp_trip_report_location_unlocked_title')}
                    </div>
                    <ul className="mt-1 space-y-1">
                      {unlockedLocations.map((location) => (
                        <li key={location.id}>
                          {t('exp_trip_report_location_unlocked_item', {
                            location: pick(location.name),
                          })}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </section>
            )}

            <section className="mb-3 rounded-md border border-stone-800 bg-stone-800/60 p-3">
              <div className="mb-2 flex items-start justify-between gap-2">
                <div className="text-[10px] uppercase tracking-wider text-stone-500">
                  {t('exp_trip_report_loot')}
                </div>
                <div className="min-w-0 text-right">
                  {usedVehicleDef ? (
                    <div className="flex flex-col items-end gap-1">
                      <div className="flex max-w-[min(100%,14rem)] flex-wrap items-baseline justify-end gap-x-2 gap-y-0.5">
                        <span className="shrink-0 text-[9px] uppercase tracking-wider text-stone-500">
                          {t('exp_trip_report_loot_yield_team_label')}
                        </span>
                        <span
                          className={`text-[10px] font-mono tabular-nums ${
                            usedPersonalLootYieldPct > 0
                              ? 'text-emerald-400'
                              : usedPersonalLootYieldPct < 0
                                ? 'text-red-400'
                                : 'text-stone-400'
                          }`}
                        >
                          {t('assign_loot_yield_pct', {
                            sign: usedPersonalLootYieldPct >= 0 ? '+' : '-',
                            pct: Math.abs(usedPersonalLootYieldPct),
                          })}
                        </span>
                      </div>
                      <div className="flex max-w-[min(100%,14rem)] flex-wrap items-baseline justify-end gap-x-2 gap-y-0.5">
                        <span className="shrink-0 text-[9px] uppercase tracking-wider text-stone-500">
                          {t('exp_trip_report_loot_yield_vehicle_label')}
                        </span>
                        <span className="text-[10px] font-mono tabular-nums text-emerald-500/90">
                          {t('vehicle_loot_pct', vehicleLootYieldParams(usedVehicleLootMultiplier))}
                        </span>
                      </div>
                      {usedTotalLootYieldPct < 0 && (
                        <div className="text-[9px] text-amber-400">
                          {t('exp_trip_report_loot_needs_warning')}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div>
                      <div
                        className={`text-[10px] font-mono tabular-nums ${
                          usedTotalLootYieldPct > 0
                            ? 'text-emerald-400'
                            : usedTotalLootYieldPct < 0
                              ? 'text-red-400'
                              : 'text-stone-400'
                        }`}
                      >
                        {t('assign_loot_yield_pct', {
                          sign: usedTotalLootYieldPct >= 0 ? '+' : '-',
                          pct: Math.abs(usedTotalLootYieldPct),
                        })}
                        {usedTotalLootYieldPct < 0 && (
                          <span className="pl-2 text-[10px] text-amber-400">
                            {t('exp_trip_report_loot_needs_warning')}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {report.collectedItems.length === 0 ? (
                <div className="text-sm text-stone-500">{t('toast_nothing')}</div>
              ) : (
                <ul className="space-y-2">
                  <AnimatePresence initial={false}>
                    {report.collectedItems
                      .filter((stack) => visibleLootDefIds.includes(stack.defId))
                      .map((stack) => (
                    <motion.li
                      key={stack.defId}
                      layout
                      initial={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -6, scale: 0.98 }}
                      transition={{ duration: 0.12 }}
                    >
                      {(() => {
                        const def = getItemDef(stack.defId)
                        const label = def ? pick(def.name) : stack.defId
                        return (
                      <button
                        type="button"
                        onClick={() => toggleDiscard(stack)}
                        disabled={isCompleting}
                        className={`flex w-full items-center justify-between rounded-md border px-3 py-2 text-left hover:border-stone-700 transition ${
                          discarded[stack.defId]
                            ? 'border-red-900/60 bg-red-950/20'
                            : 'border-stone-800 bg-stone-950'
                        }`}
                      >
                        <span
                          className={`text-sm ${discarded[stack.defId] ? 'line-through' : ''} ${
                            discarded[stack.defId]
                              ? 'text-red-300'
                              : def
                                ? RARITY_COLOR[def.rarity]
                                : 'text-stone-200'
                          }`}
                        >
                          {stack.quantity}× {label}
                          {def && (
                            <span
                              className={`ml-2 text-[11px] ${
                                discarded[stack.defId] ? 'text-red-300/70 opacity-20' : 'text-stone-500'
                              }`}
                            >
                              {itemCategoryLabel(def.category).toLowerCase()}
                            </span>
                          )}
                        </span>
                        <span className="text-[10px] uppercase tracking-wider">
                          {discarded[stack.defId]
                            ? t('exp_trip_report_discarded')
                            : ''}
                        </span>
                      </button>
                        )
                      })()}
                    </motion.li>
                  ))}
                  </AnimatePresence>
                </ul>
              )}
            </section>

            {foundSurvivor && (
              <section className="mb-4 rounded-md border border-stone-800 bg-stone-800/60 p-3">
                <div className="mb-2 text-[10px] uppercase tracking-wider text-stone-500">
                  {t('exp_trip_report_found_survivor')}
                </div>
                <div className="flex items-start gap-2.5">
                  <div className="h-14 w-14 shrink-0 overflow-hidden rounded-md border border-stone-700 bg-linear-to-br from-stone-800 to-stone-950">
                    {getPortraitSrc(foundSurvivor.portrait) ? (
                      <img
                        src={getPortraitSrc(foundSurvivor.portrait) ?? ''}
                        alt={foundSurvivor.name}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="grid h-full w-full place-items-center font-mono text-sm font-bold text-stone-300">
                        {foundSurvivor.name.slice(0, 2).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-stone-100">{foundSurvivor.name}</div>
                    <div className="text-xs text-stone-500">{foundSurvivor.role}</div>
                    <p className="mt-1 text-xs italic text-stone-400">{pick(foundSurvivor.bio)}</p>
                  </div>
                </div>
                <div className="mt-2 space-y-2 text-xs text-stone-300">
                  {foundSurvivorSkillIds.map((skillId) => {
                    const level = getSkillLevel(foundSurvivor, skillId)
                    const skillDotCount = 10
                    const filled = Math.max(0, Math.min(skillDotCount, level))
                    return (
                      <div key={skillId} className="flex items-center justify-between gap-3">
                        <span className="text-[11px] text-stone-300">
                          {t(`skill_${skillId}` as never)}
                        </span>
                        <span className="flex min-w-0 items-center justify-end gap-1.5">
                          <span className="shrink-0 text-[10px] uppercase tracking-wide text-stone-500">
                            {t('detail_survivor_level', { level })}
                          </span>
                          <span className="flex flex-wrap items-center justify-end gap-1">
                            {Array.from({ length: skillDotCount }).map((_, idx) => (
                              <span
                                key={idx}
                                className={`h-2.5 w-2.5 shrink-0 rounded-[3px] ring-1 ring-inset ${
                                  idx < filled
                                    ? 'bg-amber-500 ring-amber-300/40'
                                    : 'bg-stone-800 ring-stone-700/80'
                                }`}
                              />
                            ))}
                          </span>
                        </span>
                      </div>
                    )
                  })}
                </div>
                {rosterFull ? (
                  <p className="mt-2 text-[11px] text-amber-500/95">
                    {t('exp_trip_report_roster_full', { cap: rosterCap })}
                  </p>
                ) : null}
                <div className="mt-2 flex gap-2">
                  <button
                    type="button"
                    disabled={rosterFull}
                    onClick={() => toggleRecruit(true)}
                    className={`flex-1 rounded border px-3 py-2 text-xs uppercase tracking-wider disabled:cursor-not-allowed disabled:opacity-40 ${
                      recruitFoundSurvivor
                        ? 'border-emerald-700 bg-emerald-950/30 text-emerald-200'
                        : 'border-stone-800 bg-stone-950 text-stone-400'
                    }`}
                  >
                    {t('exp_trip_report_recruit_yes')}
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleRecruit(false)}
                    className={`flex-1 rounded border px-3 py-2 text-xs uppercase tracking-wider ${
                      !recruitFoundSurvivor
                        ? 'border-amber-700 bg-amber-950/30 text-amber-200'
                        : 'border-stone-800 bg-stone-950 text-stone-400'
                    }`}
                  >
                    {t('exp_trip_report_recruit_no')}
                  </button>
                </div>
              </section>
            )}

            {participantNeedSections.length > 0 && (
              <section className="mb-3 rounded-md border border-stone-800 bg-stone-800/60 p-2">
                <div className="mb-1.5 text-[9px] uppercase tracking-wider text-stone-500">
                  {t('exp_trip_report_needs')}
                </div>
                <div className="space-y-3">
                  {participantNeedSections.map((section) => (
                    <div key={section.survivorId}>
                      {(report.participantOutcomes?.length ?? 0) > 1 &&
                        section.displayName && (
                          <div className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-stone-400">
                            <NeedSectionPortrait survivorId={section.survivorId} />
                            <span>{section.displayName}</span>
                          </div>
                        )}
                      <div className="space-y-2">
                        {section.rows.map((row) => (
                          <NeedMeter
                            key={`${section.survivorId}-${row.key}`}
                            label={row.label}
                            before={row.before}
                            after={row.after}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {foundVehicleDef && (
              <section className="mb-4 rounded-md border border-stone-800 bg-stone-800/60 p-3">
                <div className="mb-2 text-[10px] uppercase tracking-wider text-stone-500">
                  {t('exp_trip_report_found_vehicle')}
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-2xl">{foundVehicleDef.glyph}</span>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-stone-100">
                      {pick(foundVehicleDef.name)}
                    </div>
                    <p className="text-xs italic text-stone-400">
                      {pick(foundVehicleDef.blurb)}
                    </p>
                    <div className="mt-1 text-[10px] text-emerald-400">
                      {t(
                        'assign_loot_yield_pct',
                        vehicleLootYieldParams(foundVehicleDef.lootMultiplier),
                      )}
                    </div>
                  </div>
                </div>
                {!garageBuilt ? (
                  <p className="mt-2 rounded border border-amber-900/50 bg-amber-950/20 px-2 py-1 text-xs text-amber-300">
                    {t('exp_trip_report_vehicle_no_garage')}
                  </p>
                ) : !garageHasRoom ? (
                  <div className="mt-2 space-y-2">
                    <p className="rounded border border-red-900/50 bg-red-950/20 px-2 py-1 text-xs text-red-300">
                      {t('exp_trip_report_vehicle_garage_full')}
                    </p>
                    <p className="text-[11px] text-stone-500">{t('exp_trip_report_vehicle_scrap_hint')}</p>
                    {scrapableParkedVehicles.length > 0 ? (
                      <button
                        type="button"
                        onClick={() => {
                          setManageScrapConfirmId(null)
                          setGarageManageOpen(true)
                        }}
                        className="w-full rounded border border-amber-800/70 bg-amber-950/30 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-amber-200 hover:bg-amber-950/50"
                      >
                        {t('exp_trip_report_manage_garage')}
                      </button>
                    ) : (
                      <p className="text-[11px] text-stone-600 italic">
                        {t('exp_trip_report_vehicle_all_on_mission')}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="mt-2 flex gap-2">
                    <button
                      type="button"
                      onClick={() => setStoreFoundVehicle(true)}
                      className={`flex-1 rounded border px-3 py-2 text-xs uppercase tracking-wider ${
                        storeFoundVehicle
                          ? 'border-emerald-700 bg-emerald-950/30 text-emerald-200'
                          : 'border-stone-800 bg-stone-950 text-stone-400'
                      }`}
                    >
                      {t('exp_trip_report_vehicle_store')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setStoreFoundVehicle(false)}
                      className={`flex-1 rounded border px-3 py-2 text-xs uppercase tracking-wider ${
                        !storeFoundVehicle
                          ? 'border-amber-700 bg-amber-950/30 text-amber-200'
                          : 'border-stone-800 bg-stone-950 text-stone-400'
                      }`}
                    >
                      {t('exp_trip_report_vehicle_discard')}
                    </button>
                  </div>
                )}
              </section>
            )}

            <button
              type="button"
              onClick={completeAfterLootAnimation}
              disabled={isCompleting}
              className="w-full rounded-md border border-amber-700 bg-amber-900/40 px-3 py-3 text-sm font-semibold uppercase tracking-wider text-amber-100 hover:bg-amber-900/70 disabled:cursor-wait disabled:opacity-70"
            >
              {t('exp_trip_report_complete')}
            </button>
            </div>

          </motion.div>

          {garageManageOpen && report?.foundVehicleDefId && (
            <div
              className="absolute inset-0 z-[70] flex items-center justify-center bg-black/80 p-4"
              onClick={(e) => {
                e.stopPropagation()
                setGarageManageOpen(false)
                setManageScrapConfirmId(null)
              }}
              role="presentation"
            >
              <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="trip-report-garage-manage-title"
                className="max-h-[min(72dvh,520px)] w-full max-w-sm overflow-y-auto rounded-xl border border-stone-600 bg-stone-950 p-4 shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                <h4
                  id="trip-report-garage-manage-title"
                  className="text-sm font-semibold text-stone-100"
                >
                  {t('exp_trip_report_manage_garage_title')}
                </h4>
                <p className="mt-1 text-xs text-stone-400">{t('exp_trip_report_manage_garage_body')}</p>
                <p className="mt-2 font-mono text-[10px] text-stone-500">
                  {t('garage_slots_line', {
                    used: totalGarageSlotsUsed(garageVehicles),
                    max: maxGarageSlots(garageLevel),
                  })}
                </p>
                <ul className="mt-3 space-y-2">
                  {scrapableParkedVehicles.map((v) => {
                    const def = getVehicleDef(v.defId)
                    if (!def) return null
                    const preview = scrapYieldForVehicleDef(v.defId)
                      .map((s) => {
                        const itemDef = getItemDef(s.defId)
                        const label = itemDef ? pick(itemDef.name) : s.defId
                        return `${s.quantity}× ${label}`
                      })
                      .join(', ')
                    const confirming = manageScrapConfirmId === v.id
                    return (
                      <li
                        key={v.id}
                        className="rounded-md border border-stone-800 bg-stone-900/80 p-2.5"
                      >
                        <div className="flex items-start gap-2">
                          <span className="text-xl">{def.glyph}</span>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-stone-100">{pick(def.name)}</div>
                            <p className="mt-0.5 text-[10px] text-stone-500">
                              {t('garage_scrap_yield_label')} {preview}
                            </p>
                          </div>
                        </div>
                        {confirming ? (
                          <div className="mt-2 flex gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                scrapVehicle(v.id)
                                setManageScrapConfirmId(null)
                                const next = useGameStore.getState()
                                const gl =
                                  next.workstations.garage?.built === true
                                    ? Math.max(1, next.workstations.garage.level)
                                    : 0
                                if (
                                  report.foundVehicleDefId &&
                                  garageCanFitVehicle(
                                    gl,
                                    next.garageVehicles,
                                    report.foundVehicleDefId,
                                  )
                                ) {
                                  setStoreFoundVehicle(true)
                                  setGarageManageOpen(false)
                                }
                              }}
                              className="flex-1 rounded border border-red-800/70 bg-red-950/40 px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-red-200 hover:bg-red-950/60"
                            >
                              {t('exp_trip_report_manage_garage_confirm_scrap')}
                            </button>
                            <button
                              type="button"
                              onClick={() => setManageScrapConfirmId(null)}
                              className="rounded border border-stone-700 px-2 py-1.5 text-[10px] uppercase tracking-wider text-stone-400 hover:bg-stone-800"
                            >
                              {t('garage_scrap_cancel')}
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setManageScrapConfirmId(v.id)}
                            className="mt-2 w-full rounded border border-stone-700 py-1.5 text-[10px] uppercase tracking-wider text-stone-300 hover:border-amber-800/50 hover:text-amber-200"
                          >
                            {t('exp_trip_report_manage_garage_scrap')}
                          </button>
                        )}
                      </li>
                    )
                  })}
                </ul>
                <button
                  type="button"
                  onClick={() => {
                    setGarageManageOpen(false)
                    setManageScrapConfirmId(null)
                  }}
                  className="mt-4 w-full rounded border border-stone-700 py-2 text-xs uppercase tracking-wider text-stone-400 hover:bg-stone-800"
                >
                  {t('exp_trip_report_manage_garage_close')}
                </button>
              </div>
            </div>
          )}

          {showUnlockedPopup && unlockedLocations[0] && (
            <div
              className="fixed inset-0 z-60 flex items-center justify-center bg-black/70 p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-full max-w-sm rounded-lg border border-emerald-700/70 bg-stone-950 p-4 text-center shadow-xl">
                <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-300">
                  {t('exp_trip_report_location_unlocked_popup_title')}
                </div>
                <div className="mt-2 text-base font-semibold text-stone-100">
                  {t('exp_trip_report_location_unlocked_popup_location', {
                    location: pick(unlockedLocations[0].name),
                  })}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    onConfirmUnlockedLocations(unlockedLocations.map((location) => location.id))
                    setShowUnlockedPopup(false)
                  }}
                  className="mt-4 w-full rounded-md border border-emerald-700 bg-emerald-900/40 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-emerald-100 hover:bg-emerald-900/70"
                >
                  {t('exp_trip_report_location_unlocked_popup_confirm')}
                </button>
              </div>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  )
}

/** Same rounding/clamp as NeedMeter — used to hide rows with no visible change */
function needValuesDiffer(before: number, after: number): boolean {
  const b = Math.max(0, Math.min(100, Math.round(before)))
  const a = Math.max(0, Math.min(100, Math.round(after)))
  return b !== a
}

function NeedSectionPortrait({ survivorId }: { survivorId: string }) {
  const survivors = useGameStore((s) => s.survivors)
  const survivor = survivors.find((s) => s.id === survivorId)
  const portraitSrc = getPortraitSrc(survivor?.portrait)
  const initials = survivor?.name.slice(0, 2).toUpperCase() ?? '??'
  return (
    <span className="h-5 w-5 shrink-0 overflow-hidden rounded-md border border-stone-700 bg-linear-to-br from-stone-800 to-stone-950">
      {portraitSrc ? (
        <img src={portraitSrc} alt={survivor?.name ?? '??'} className="h-full w-full object-cover" />
      ) : (
        <span className="grid h-full w-full place-items-center font-mono text-[5px] font-bold text-stone-300">
          {initials.slice(0, 1)}
        </span>
      )}
    </span>
  )
}

function ReturnedTeamPortrait({ team }: { team: Survivor[] }) {
  if (team.length <= 1) {
    const lead = team[0]
    const portraitSrc = getPortraitSrc(lead?.portrait)
    const initials = lead?.name.slice(0, 2).toUpperCase() ?? '??'
    return (
      <div className="h-10 w-10 shrink-0 overflow-hidden rounded-md border border-stone-700 bg-linear-to-br from-stone-800 to-stone-950">
        {portraitSrc ? (
          <img src={portraitSrc} alt={lead?.name ?? '??'} className="h-full w-full object-cover" />
        ) : (
          <div className="grid h-full w-full place-items-center font-mono text-xs font-bold text-stone-300">
            {initials}
          </div>
        )}
      </div>
    )
  }

  if (team.length === 2) {
    return (
      <div className="grid h-12 w-12 shrink-0 grid-cols-1 gap-0.5 overflow-y-hidden rounded-md border border-stone-700 bg-stone-900 p-0.5">
        {team.map((survivor) => {
          const portraitSrc = getPortraitSrc(survivor.portrait)
          const initials = survivor.name.slice(0, 2).toUpperCase()
          return (
            <div
              key={survivor.id}
              className="h-5.5 overflow-hidden rounded-[3px] bg-linear-to-br from-stone-800 to-stone-950"
            >
              {portraitSrc ? (
                <img src={portraitSrc} alt={survivor.name} className="h-full w-full object-cover object-center" />
              ) : (
                <div className="grid h-full w-full place-items-center font-mono text-[8px] font-bold text-stone-300">
                  {initials}
                </div>
              )}
            </div>
          )
        })}
      </div>
    )
  }

  const useThreeRows = team.length > 4
  const useOverflowSlot = team.length > 5
  const portraits = useOverflowSlot ? team.slice(0, 5) : team.slice(0, 6)
  const overflowCount = team.length - portraits.length

  return (
    <div
      className={`grid w-12 overflow-hidden max-h-11 shrink-0 gap-0 rounded-md border border-stone-700 bg-stone-900 p-0.5 ${
        useThreeRows ? 'grid-cols-2 grid-rows-3' : 'grid-cols-2 grid-rows-2'
      }`}
    >
      {portraits.map((survivor) => {
        const portraitSrc = getPortraitSrc(survivor.portrait)
        const initials = survivor.name.slice(0, 2).toUpperCase()
        return (
          <div
            key={survivor.id}
            className="h-5 overflow-hidden rounded-[3px] bg-linear-to-br from-stone-800 to-stone-950"
          >
            {portraitSrc ? (
              <img src={portraitSrc} alt={survivor.name} className="h-full w-full object-cover" />
            ) : (
              <div className="grid h-full w-full place-items-center font-mono text-[7px] font-bold text-stone-300">
                {initials}
              </div>
            )}
          </div>
        )
      })}
      {useOverflowSlot && (
        <div className="grid h-3.5 place-items-center rounded-[3px] bg-stone-800 text-[8px] font-semibold text-stone-200">
          +{overflowCount}
        </div>
      )}
    </div>
  )
}

const RARITY_WEIGHT: Record<'common' | 'uncommon' | 'special', number> = {
  common: 0,
  uncommon: 1,
  special: 2,
}

function pickLootTeaserItems(items: ItemStack[]): ItemStack[] {
  return [...items].sort((a, b) => {
    const aDef = getItemDef(a.defId)
    const bDef = getItemDef(b.defId)
    const rarityDiff = (bDef ? RARITY_WEIGHT[bDef.rarity] : -1) - (aDef ? RARITY_WEIGHT[aDef.rarity] : -1)
    if (rarityDiff !== 0) return rarityDiff
    const valueDiff = (bDef?.value ?? 0) - (aDef?.value ?? 0)
    if (valueDiff !== 0) return valueDiff
    return b.quantity - a.quantity
  })
}

/** 0–100 meter: green = current (after); red = lost span when before &gt; after; brighter green = gain when after &gt; before */
function NeedMeter({
  label,
  before,
  after,
}: {
  label: string
  before: number
  after: number
}) {
  const b = Math.max(0, Math.min(100, Math.round(before)))
  const a = Math.max(0, Math.min(100, Math.round(after)))
  const delta = a - b

  return (
    <div className="space-y-0.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[9px] font-medium uppercase tracking-wider text-stone-500">
          {label}
        </span>
        <span className="min-w-0 text-right font-mono text-[9px] tabular-nums text-stone-400">
          <span className="hidden sm:inline">
            <span className="text-stone-600">{b}</span>
            <span className="mx-0.5 text-stone-700">→</span>
            <span className="text-stone-200">{a}</span>
            <span
              className={`ml-1 ${
                delta > 0 ? 'text-emerald-400' : delta < 0 ? 'text-red-400' : 'text-stone-600'
              }`}
            >
              ({delta > 0 ? '+' : ''}
              {delta})
            </span>
          </span>
          <span
            className={`sm:hidden ${
              delta > 0 ? 'text-emerald-400' : delta < 0 ? 'text-red-400' : 'text-stone-600'
            }`}
            title={`${b} → ${a} (${delta > 0 ? '+' : ''}${delta})`}
          >
            {delta > 0 ? '+' : ''}
            {delta}
          </span>
        </span>
      </div>
      <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-stone-800/90 ring-1 ring-inset ring-stone-700/60">
        {b > a ? (
          <>
            <div
              className="absolute left-0 top-0 z-10 h-full bg-emerald-600"
              style={{ width: `${a}%` }}
              title={`${label}: ${a}`}
            />
            <div
              className="absolute top-0 z-20 h-full bg-red-500 shadow-[inset_0_0_0_1px_rgba(0,0,0,0.15)]"
              style={{ left: `${a}%`, width: `${b - a}%` }}
              title={`${label} lost: ${b - a}`}
            />
          </>
        ) : a > b ? (
          <>
            <div
              className="absolute left-0 top-0 z-10 h-full bg-emerald-700"
              style={{ width: `${b}%` }}
            />
            <div
              className="absolute top-0 z-20 h-full bg-emerald-400"
              style={{ left: `${b}%`, width: `${a - b}%` }}
            />
          </>
        ) : (
          <div
            className="absolute left-0 top-0 h-full bg-emerald-600"
            style={{ width: `${a}%` }}
          />
        )}
      </div>
    </div>
  )
}
